import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';

const PLACEHOLDER_PHOTOS = [
  'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
  'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=400&q=80',
  'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=400&q=80',
];

/** Three birthday photos in alternating up/down offset layout */
export function PhotoTrioSection({ content }: { content: BirthdayContent['photoTrio'] }) {
  const photos = content.photos.length >= 3
    ? content.photos.slice(0, 3)
    : PLACEHOLDER_PHOTOS;

  const offsets = ['translateY(0px)', 'translateY(-32px)', 'translateY(16px)'];
  const rotations = ['-rotate-2', 'rotate-0', 'rotate-2'];
  const delays = [0, 0.15, 0.3];

  return (
    <section
      className="relative py-20 px-6 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%)' }}
    >
      {/* section label */}
      <div className="text-center mb-12">
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: BDAY_EASE }}
          className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
        >
          Moments
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
          className="text-3xl font-bold text-white"
        >
          Birthday Snapshots
        </motion.h2>
      </div>

      {/* trio layout */}
      <div className="max-w-5xl mx-auto flex items-center justify-center gap-6 md:gap-10">
        {photos.map((url, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.75, delay: delays[i], ease: BDAY_EASE }}
            whileHover={{ scale: 1.05, rotate: 0 }}
            style={{ transform: offsets[i] }}
            className={`relative flex-shrink-0 ${rotations[i]}`}
          >
            {/* glow ring */}
            <div className="absolute -inset-2 rounded-2xl bg-blue-500/20 blur-xl" />

            <div className="relative w-44 h-56 md:w-56 md:h-72 rounded-2xl overflow-hidden border-2 border-blue-400/30 shadow-2xl shadow-blue-900/60">
              <img
                src={url}
                alt={`Birthday photo ${i + 1}`}
                className="w-full h-full object-cover"
              />
              {/* shimmer overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-transparent to-transparent" />
            </div>

            {/* floating emoji badge */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2.5 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-3 -right-3 bg-blue-500 rounded-full w-9 h-9 flex items-center justify-center text-lg shadow-lg"
            >
              {['🎂', '✨', '🎉'][i]}
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* decorative line */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
    </section>
  );
}
